import * as React from 'react';
import { 
    Dropdown,
    ToolbarButton,
    makeStyles,
    Option,
    OptionOnSelectData,
    Textarea,
    Text,
    Input,
    InfoLabel,
    Field,
    InputOnChangeData,
    MessageBar,
    MessageBarBody,
    MessageBarTitle,
    Button,
    Tooltip,
    MessageBarActions
} from '@fluentui/react-components';
import {
    ArrowCircleLeft24Regular,
    DeleteRegular,
    DismissRegular,
    PresenceAvailableRegular,
    PresenceBlockedRegular,
    Save24Regular
} from "@fluentui/react-icons";
import { CustomProperty, Product, PropertyType } from '../../dto/Product';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GenericToolbar } from '../../components/GenericToolbar';
import { formStyle, formInputStyle, flexRow, flexColumn, formInputInlineStyle, infoLabel, growFlexColumn, flexRowWrap, growFlexRowWrap } from '../../styles/GlobalStyles';
import { useGlobalDispatch } from '../../state/GlobalStateProvider';
import { License, LifeTime, LifeTimeKind, LifeTimeMode, LifeTimeUnit } from '../../dto/License';
import { Customer, EnvironmentType } from '../../dto/Customer';
import { CopyButton } from '../../components/CopyButton';
import { FormBase } from './FormBase';
import { fetchRecord, submit } from '../../util/FormUtils';
import { LicenseExplorer } from '../../components/LicenseExplorer';
import { ensureSuccess, handleError } from '../../util/ErrorHandling';
import { Empty } from '../../util/Guid';
import { RecordReference } from '../../dto/RecordReference';
import { capitalizeFirstChar } from '../../util/StringUtils';
import { addToUtcDate, toUtcIso } from '../../util/DateUtils';
import { usePaddlePriceId } from '../../util/PaddleEnv';
import { Telemetry } from '../../dto/Telemetry';
import { ChartProps, CustomizedCalloutData, DataVizPalette, LineChart } from '@fluentui/react-charts';
import { useResizeObserver } from '../../util/ResponsiveDesign';
import { IDatabaseRecord } from '../../dto/IDatabaseRecord';
import { TriStateBoolDropdown } from '../../components/TriStateBoolDropdown';
import { RetrieveMultipleResponse } from '../../dto/RetrieveMultipleResponse';
import { FluentDialog } from '../../components/FluentDialog';

const entitySet = "licenses";

const useStyles = makeStyles({
    formInput: formInputStyle,
    formInputInline: formInputInlineStyle,
    form: formStyle,
    flexRow: flexRow,
    flexRowWrap: flexRowWrap,
    growFlexRowWrap: growFlexRowWrap,
    flexColumn: flexColumn,
    growFlexColumn: growFlexColumn,
    infoLabel: infoLabel,
    grow: {
        flex: 1
    }
});

const defaultRecord: Partial<License> = {
    lifeTime: {
        kind: "relative",
        unit: "month",
        mode: "simple",
        value: 1
    },
    source: "internal"
};

export const LicenseForm: React.FC<any> = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id, publisherId } = useParams();
    const priceIdDictionary = usePaddlePriceId();

    const globalDispatch = useGlobalDispatch();
    const [isSaving, setIsSaving] = React.useState(false);
    const [record, setRecord] = React.useState<License>({} as License);

    const [customers, setCustomers] = React.useState<Array<Customer>>([] as Array<Customer>);
    const [products, setProducts] = React.useState<Array<Product>>([] as Array<Product>);
    const [telemetry, setTelemetry] = React.useState<Array<Telemetry>>([] as Array<Telemetry>);

    const [isConfirmingDeletion, setIsConfirmingDeletion] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [deletionError, setDeletionError] = React.useState("");

    const styles = useStyles();
    const [ref, rect] = useResizeObserver();

    const customProperties: Array<CustomProperty> = React.useMemo(() => record.customProperties ?? [], [record.customProperties]);

    const missingEnvironments: Array<EnvironmentType> = React.useMemo(() =>
    {
        if (id && id !== "new")
        {
            return [];
        }

        if (!record.productId || !record.productId.guid)
        {
            return [];
        }

        const missingEnvironments: EnvironmentType[] = [];
        const product = products.find(p => p.guid === record.productId?.guid);

        if (product && product.componentTypes && product.componentTypes.length)
        {
            if (product.componentTypes.includes("canvas app") && ( record.environments == null || !record.environments.some(e => e.type === "entra")))
            {
                missingEnvironments.push("entra");
            }
            if ((product.componentTypes.includes("pcf") || product.componentTypes.includes("plugin") || product.componentTypes.includes("flow")) && ( record.environments == null || !record.environments.some(e => e.type === "dataverse")))
            {
                missingEnvironments.push("dataverse");
            }
        }

        return missingEnvironments;
    }, [id, products, record.environments, record.productId]);

    const isExisting = !!record?.guid && record.guid !== "new";
    const isDisabled = isExisting && record.state === "inactive";
    const isUserAllowedToSave = React.useMemo(() => (!record.guid || ["contributor", "admin"].includes(record.currentUserAccessLevel)), [record]);
    const isEditable = React.useMemo(() => !isDisabled && isUserAllowedToSave, [isDisabled, isUserAllowedToSave]);
    const areRequiredFieldsMissing = React.useMemo(() => !!missingEnvironments.length || !record.productId?.guid || !record.productVersionId?.guid || !record.customerId?.guid || !record.environments?.length || !record.lifeTime || (record.lifeTime.kind === "relative" && (!record.lifeTime.unit || !record.lifeTime.value)) || (record.lifeTime.kind === "absolute" && !record.lifeTime.date) || (record.customProperties ?? []).some(p => p.requirementLevel === "required" && p.value == null), [missingEnvironments.length, record.customProperties, record.customerId?.guid, record.environments?.length, record.lifeTime, record.productId?.guid, record.productVersionId?.guid]);
    const isSaveDisabled = React.useMemo(() => isSaving || areRequiredFieldsMissing || !isEditable, [isSaving, areRequiredFieldsMissing, isEditable]);

    const showPaymentBanner = React.useMemo(() =>
    {
        return !!record.guid && !record.key && !record.transactionId;
    }, [record.guid, record.key, record.transactionId]);

    const showRefreshBanner = React.useMemo(() =>
    {
        return !!record.guid && !record.key && !!record.transactionId;
    }, [record.guid, record.key, record.transactionId]);

    const onProductChange = React.useCallback((event: any, data: OptionOnSelectData) => {
        const product = products.find(p => p.guid === data.optionValue!);
        
        setRecord((record) => ({
            ...record,
            productId: {
                guid: data.optionValue!,
                type: "product",
                name: data.optionText!
            },
            productVersionId: undefined,
            customProperties: product?.customProperties?.map(p => {
                // Check if there's a default value for internal (self-service would use external)
                const defaultValue = p.defaultValues?.internal;
                
                return {
                    ...p,
                    value: defaultValue !== undefined ? defaultValue : null
                } as CustomProperty;
            }) ?? []
        }));
    }, [products]);

    const onProductVersionChange = React.useCallback((event: any, data: OptionOnSelectData) => {
        setRecord((record) => ({
            ...record,
            productVersionId:
            {
                guid: data.optionValue!,
                type: "productversion",
                name: data.optionText!
            }}));
    }, []);

    const onCustomerChange = React.useCallback((event: any, data: OptionOnSelectData) => {
        setRecord((record) => ({
            ...record,
            customerId: { guid: data.optionValue!, type: "customer", name: data.optionText! },
            environments: []
        }));
    }, []);

    const onCustomerEnvironmentsChange = React.useCallback((event: any, data: OptionOnSelectData) => {
        setRecord((record) => ({
            ...record,
            environments: customers.find(c => c.guid === record.customerId?.guid)?.environments?.filter(env => data.selectedOptions.includes(env.guid)) ?? []
        }));
    }, [customers]);

    const onLifeTimeChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        const kind = data.optionValue! as LifeTimeKind;

        let newLifeTime: LifeTime;

        if (kind === "relative")
        {
            newLifeTime = {
                kind: "relative",
                unit: "month",
                mode: "simple",
                value: 1
            };
        }
        else if(kind === "absolute")
        {
            newLifeTime = {
                kind: "absolute",
                mode: "end",
                date: addToUtcDate(new Date(Date.now()), { months: 1}).toISOString().split('T')[0]
            };
        }
        else
        {
            newLifeTime = {
                kind: "unlimited"
            };
        }

        setRecord((record) => ({...record, lifeTime: newLifeTime}));
    }, []);

    const onLifeTimeModeChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        const mode = data.optionValue! as LifeTimeMode;

        setRecord((record) =>
        {
            if (record.lifeTime?.kind === "relative")
            {
                return {
                    ...record,
                    lifeTime: {
                        kind: "relative",
                        unit: record.lifeTime?.unit,
                        value: record.lifeTime?.value,
                        mode: mode,
                        time: record.lifeTime?.time
                    }
                };
            }
            else if (record.lifeTime?.kind === "absolute")
            {
                return {
                    ...record,
                    lifeTime: {
                        kind: "absolute",
                        date: record.lifeTime?.date,
                        mode: mode,
                        time: record.lifeTime?.time
                    }
                };
            }
            else
            {
                return record;
            }
        });
    }, []);

    const onLifeTimeTimeChange = React.useCallback((event: any, data: InputOnChangeData) =>
    {
        const time = data.value;

        setRecord((record) =>
        {
            if (record.lifeTime?.kind === "relative")
            {
                return {
                    ...record,
                    lifeTime: {
                        kind: "relative",
                        unit: record.lifeTime?.unit,
                        value: record.lifeTime?.value,
                        mode: record.lifeTime?.mode,
                        time: time
                    }
                };
            }
            else if (record.lifeTime?.kind === "absolute")
            {
                return {
                    ...record,
                    lifeTime: {
                        kind: "absolute",
                        date: record.lifeTime?.date,
                        mode: record.lifeTime?.mode,
                        time: time
                    }
                };
            }
            else
            {
                return record;
            }
        });
    }, []);

    const onLifeTimeDateChange = React.useCallback((event: any, data: InputOnChangeData) =>
    {
        setRecord((record) => (
            {
                ...record,
                lifeTime:
                {
                    kind: "absolute",
                    mode: (record.lifeTime?.kind === "absolute" || record.lifeTime?.kind === "relative") ? record.lifeTime.mode : undefined,
                    date: data.value
                }
            }
        ));
    }, []);

    const onLifeTimeUnitChange = React.useCallback((event: any, data: OptionOnSelectData) =>
    {
        const unit = data.optionValue! as LifeTimeUnit;

        setRecord((record) => (
            {
                ...record,
                lifeTime:
                {
                    kind: "relative",
                    unit: unit,
                    mode: (record.lifeTime?.kind === "absolute" || record.lifeTime?.kind === "relative") ? record.lifeTime.mode : undefined,
                    value: record.lifeTime?.kind === "relative" ? record.lifeTime.value ?? 1 : 1
                }
            }
        ));
    }, []);

    const onLifeTimeValueChange = React.useCallback((ev: React.ChangeEvent<HTMLInputElement>, data: InputOnChangeData) =>
    {
        setRecord((record) => (
            {
                ...record,
                lifeTime:
                {
                    kind: "relative",
                    unit: record.lifeTime?.kind === "relative" ? record.lifeTime.unit : undefined,
                    mode: (record.lifeTime?.kind === "absolute" || record.lifeTime?.kind === "relative") ? record.lifeTime.mode : undefined,
                    value: parseInt(data.value)
                }
            }
        ))
    }, []);

    const onCustomPropertyChange = React.useCallback((customPropertyName: string, value: any) =>
    {
        // NaN is the only value not equal to itself, so if we were passed NaN, we don't want to do anything
        // eslint-disable-next-line no-self-compare
        if (value !== value)
        {
            return;
        }

        setRecord((record) => (
            {
                ...record,
                customProperties: record.customProperties?.map(p => p.name === customPropertyName ? ({...p, value: value}) : p)
            }
        ))
    }, []);

    const onBack = React.useCallback(() =>
    {
        navigate(`/workspace/publishers/${publisherId}/${entitySet}`);
    }, [navigate, publisherId]);

    const onSubmit = React.useCallback(async () =>
    {
        if (!isSaveDisabled)
        {
            await submit(publisherId, id, record, entitySet, setRecord, setIsSaving, navigate, globalDispatch);
        }
    }, [globalDispatch, id, isSaveDisabled, navigate, publisherId, record]);

    const onStateToggle = React.useCallback(async () =>
    {
        if (!isDisabled)
        {
            const update = ({...record, state: "inactive"} as IDatabaseRecord);
            await submit(publisherId, id, update, entitySet, setRecord, setIsSaving, navigate, globalDispatch);
        }
        else
        {
            const update = ({...record, state: "active"} as IDatabaseRecord);
            await submit(publisherId, id, update, entitySet, setRecord, setIsSaving, navigate, globalDispatch);
        }
    }, [globalDispatch, id, isDisabled, navigate, publisherId, record]);

    const refresh = React.useCallback(() =>
    {
        return fetchRecord(publisherId, id, entitySet, setRecord, globalDispatch, defaultRecord as License);
    }, [globalDispatch, id, publisherId]);

    const fetchTelemetry = React.useCallback(async (publisherId: string | undefined, id: string | undefined) =>
    {
        if (!publisherId || !id || id === "new")
        {
            return;
        }

        const response = await fetch(`/api/publishers/${publisherId}/${entitySet}/${id}/telemetry`, {
            headers: {
                "Content-Type": "application/json"
            }
        });

        await ensureSuccess(response);

        const telemetryData = await response.json() as RetrieveMultipleResponse<Telemetry>;
        setTelemetry(telemetryData.records);
    }, []);

    const onDelete = React.useCallback(() =>
    {
        setIsConfirmingDeletion(true);
    }, []);

    const clearDeletionError = React.useCallback(() =>
    {
        setDeletionError("");
    }, []);

    const cancelDeletionDialog = React.useCallback(() =>
    {
        clearDeletionError();
        setIsConfirmingDeletion(false);
    }, [clearDeletionError]);

    const submitDeletionDialog = React.useCallback(async () =>
    {
        try
        {
            setIsDeleting(true);

            const response = await fetch(`/api/publishers/${publisherId}/${entitySet}/${id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json"
                }
            });

            await ensureSuccess(response);
            
            cancelDeletionDialog();
            onBack();
        }
        catch (e)
        {
            const error = e as Error;
            setDeletionError(error.message ?? "Unspecified Error");
        }
        finally
        {
            setIsDeleting(false);
        }
    }, [cancelDeletionDialog, id, onBack, publisherId]);

    const didInitRef = React.useRef<string | null>(null);

    // This is the initialization hook
    React.useEffect(() =>
    {
        // If we already initialized once, just exit, updates supply us with the latest record state automatically
        if (didInitRef.current !== id)
        {
            if (!id)
            {
                return;
            }

            // This happens right after the transition from id === "new" to the actual id
            if (location?.state?.record?.guid === id)
            {
                // Use record from location once...
                setRecord(location.state.record);

                //...then discard so that it can never overwrite local edits
                navigate(location.pathname, {
                    replace: true,
                    state: null
                });
            }
            else
            {
                // In all other cases, just fetch
                // In all other cases, just fetch
                fetchRecord(
                    publisherId,
                    id,
                    entitySet,
                    setRecord,
                    globalDispatch,
                    defaultRecord as License
                );
                fetchTelemetry(publisherId, id);
            }

            didInitRef.current = id;
        }
    },
    [
        id,
        publisherId,
        location.pathname,
        location.state,
        navigate,
        globalDispatch,
        fetchTelemetry
    ]);

    React.useEffect(() => {
        if (publisherId && (!id || id === "new"))
        {
            fetch(`/api/publishers/${publisherId}/customers`, { 
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(ensureSuccess)
                .then(r => r.json() as Promise<RetrieveMultipleResponse<Customer>>)
                .then(r => r.records)
                .then(setCustomers)
                .catch(e => handleError(e, globalDispatch));
        }
      }, [publisherId, globalDispatch, id]);

    React.useEffect(() => {
        if (publisherId && (!id || id === "new"))
        {
            fetch(`/api/publishers/${publisherId}/products`, { 
                headers: {
                    "Content-Type": "application/json"
                }
            })
                .then(ensureSuccess)
                .then(r => r.json() as Promise<RetrieveMultipleResponse<Product>>)
                .then(r => r.records)
                .then(setProducts)
                .catch(e => handleError(e, globalDispatch));
        }
    }, [publisherId, globalDispatch, id]);

    const dialogs = React.useMemo(() => (
        <>
            <FluentDialog title={`Delete this record`} submitLabel='Confirm' open={isConfirmingDeletion} submitDisabled={isDeleting} hideCancel={isDeleting} onSubmit={submitDeletionDialog} onCancel={cancelDeletionDialog}>
                <div className={styles.form}>
                    { deletionError && (
                        <MessageBar intent="error">
                            <MessageBarBody>
                                <MessageBarTitle>An error occured: {deletionError}</MessageBarTitle>
                            </MessageBarBody>
                            <MessageBarActions
                                containerAction={
                                    <Button
                                        aria-label="dismiss"
                                        appearance="transparent"
                                        icon={<DismissRegular />}
                                        onClick={clearDeletionError}
                                    />
                                }
                            />
                        </MessageBar>
                    ) }
                    <Text>
                        Click confirm below to delete this record.
                    </Text>
                </div>
            </FluentDialog>
        </>
    ), [cancelDeletionDialog, clearDeletionError, deletionError, isConfirmingDeletion, isDeleting, styles.form, submitDeletionDialog]);

    const toolbar = React.useMemo(() => (
        <GenericToolbar
                actionButtons={
                    <>
                        <Tooltip relationship='label' content="Back">
                            <ToolbarButton
                                aria-label="Back"
                                appearance="subtle"
                                icon={<ArrowCircleLeft24Regular />}
                                onClick={onBack}
                            />
                        </Tooltip>
                        <Tooltip relationship='label' content="Save (Ctrl + S)">
                            <ToolbarButton
                                aria-label="Save"
                                appearance="primary"
                                icon={<Save24Regular />}
                                disabled={isSaveDisabled}
                                onClick={onSubmit}
                            />
                        </Tooltip>
                        <Tooltip relationship='label' content={ isDisabled ? "Enable" : "Disable"}>
                            <ToolbarButton
                                aria-label={ isDisabled ? "Enable" : "Disable"}
                                appearance="primary"
                                disabled={!isExisting || isSaving || areRequiredFieldsMissing || !isUserAllowedToSave}
                                icon={ isDisabled ? <PresenceAvailableRegular /> : <PresenceBlockedRegular />}
                                onClick={onStateToggle}
                            />
                        </Tooltip>
                        <Tooltip relationship='label' content="Delete">
                            <ToolbarButton
                                aria-label={"Delete"}
                                appearance="primary"
                                disabled={!isExisting || isSaving || record.currentUserAccessLevel !== "admin"}
                                icon={ <DeleteRegular />}
                                onClick={onDelete}
                            />
                        </Tooltip>
                    </>
                }
                farGroup={
                    <>
                        <Field
                            label={
                                <InfoLabel size="medium" className={styles.infoLabel} info="External representation of this record's id. Visible on generated licenses.">
                                    External Id
                                </InfoLabel>
                            }
                        >
                            <Input type="text" value={record.externalId ?? ""} disabled contentAfter={<CopyButton disabled={!record.externalId} value={record.externalId} />} />
                        </Field>
                    </>
                }
            />
    ), [onBack, isSaveDisabled, onSubmit, isDisabled, isExisting, isSaving, areRequiredFieldsMissing, isUserAllowedToSave, onStateToggle, record.currentUserAccessLevel, record.externalId, onDelete, styles.infoLabel]);

    const _calculateCalloutDescription = (
        calloutDataProps: CustomizedCalloutData
    ): string | undefined => {
        if (
        calloutDataProps.values.filter(
            (value) => value.legend === "Low Confidence Data*"
        ).length > 0
        ) {
        return "* This data was below our confidence threshold.";
        }
        return undefined;
    };

    const productOptions = React.useMemo(() => !!record.guid
        ? [ record.productId ?? { guid: Empty, name: "NOT FOUND"} as RecordReference ]
        : products
    , [products, record.guid, record.productId]);

    const productVersionOptions = React.useMemo(() => !!record.guid
        ? [ record.productVersionId ?? { guid: Empty, name: "NOT FOUND"} as RecordReference ]
        : products.find(p => p.guid === record.productId?.guid)?.versions ?? []
    , [products, record.guid, record.productId?.guid, record.productVersionId]);

    const customerOptions = React.useMemo(() => !!record.guid
        ? [ record.customerId ?? { guid: Empty, name: "NOT FOUND"} ]
        : customers
    , [customers, record.customerId, record.guid]);

    const customerEnvironmentOptions = React.useMemo(() => !!record.guid
        ? (record?.environments ?? [])
        : customers.find(c => c.guid === record.customerId?.guid)?.environments ?? []
    , [customers, record.customerId?.guid, record?.environments, record.guid]);

    const expiryDate = React.useMemo(() =>
    {
        const utcNow = new Date(Date.now());

        if (record.expiry)
        {
            return new Date(record.expiry);
        }
        else if ( record.lifeTime?.kind === "relative" && record.lifeTime.unit && record.lifeTime.value && record.lifeTime.mode && ( record.lifeTime.mode !== "exact" || record.lifeTime.time ) )
        {
            const targetDate = addToUtcDate(
                utcNow,
                {
                    years: record.lifeTime.unit === "year" ? record.lifeTime.value : 0,
                    months: record.lifeTime.unit === "month" ? record.lifeTime.value : 0,
                    weeks: record.lifeTime.unit === "week" ? record.lifeTime.value : 0,
                    days: record.lifeTime.unit === "day" ? record.lifeTime.value : 0
                }
            );

            if (record.lifeTime.mode === "simple")
            {
                return targetDate;
            }
            else
            {
                const targetDateString = targetDate.toISOString().split('T')[0];

                return toUtcIso(targetDateString, record.lifeTime.mode, record.lifeTime.time);
            }
        }
        else if ( record.lifeTime?.kind === "absolute" && record.lifeTime.date && record.lifeTime.mode && ( record.lifeTime.mode !== "exact" || record.lifeTime.time ) )
        {
            return toUtcIso(record.lifeTime.date, record.lifeTime.mode, record.lifeTime.time);
        }
        else
        {
            return null;
        }
    }, [record.expiry, record?.lifeTime]);

    const modeField = React.useMemo(() => {
        if (record.lifeTime?.kind === "absolute" || record.lifeTime?.kind === "relative")
        {
            return (
                <Field
                    className={styles.grow}
                    label={
                        <InfoLabel
                            required
                            info={
                                <Text>
                                    Select a mode for setting the time of the expiry date.

                                    <ul>
                                        <li><b>Simple</b>: Takes the same time that we currently have when saving</li>
                                        <li><b>Start</b>: Takes the start of the day. Caution: This considers the UTC date. The UTC date can be a different day than your local date due to timezone offsets. To verify, check the 'Calculated Expiry (UTC)' and 'Calculated Expiry (Local)' fields.</li>
                                        <li><b>End</b>: Takes the end of the day. Caution: This considers the UTC date. The UTC date can be a different day than your local date due to timezone offsets. To verify, check the 'Calculated Expiry (UTC)' and 'Calculated Expiry (Local)' fields.</li>
                                        <li><b>Exact</b>: Allows you to enter a time yourself. Caution: The time you enter is handled as UTC, without your local timezone offset. To verify, check the 'Calculated Expiry (UTC)' and 'Calculated Expiry (Local)' fields.</li>
                                    </ul>

                                    For relative expiry we recommend 'simple' mode, for absolute expiry we recommend 'end' mode. Use other types cautiously after understanding timezones and verifying using the 'Calculated Expiry' UTC and local fields.
                                </Text>
                            }
                        >
                            Mode
                        </InfoLabel>
                    }
                >
                    <Dropdown
                        disabled={!!record.guid}
                        placeholder="Choose a mode for the lifetime"
                        value={capitalizeFirstChar(record?.lifeTime?.mode) ?? ""}
                        selectedOptions={record?.lifeTime?.mode ? [record.lifeTime.mode] : []}
                        onOptionSelect={onLifeTimeModeChange}
                    >
                        <Option key="simple" value="simple">Simple</Option>
                        <Option key="start" value="start">Start</Option>
                        <Option key="end" value="end">End</Option>
                        <Option key="exact" value="exact">Exact</Option>
                    </Dropdown>
                </Field>
            )
        }
        else
        {
            return undefined;
        }
    }, [onLifeTimeModeChange, record.guid, record.lifeTime, styles.grow]);

    const timeField = React.useMemo(() =>
    {
        if ((record.lifeTime?.kind === "absolute" || record.lifeTime?.kind === "relative") && record?.lifeTime?.mode === "exact")
        {
            return (
                <Field
                    className={styles.grow}
                    label="Time"
                    required
                >
                    <Input
                        type="time"
                        disabled={!!record.guid}
                        value={record?.lifeTime?.time ?? ""}
                        onChange={onLifeTimeTimeChange}
                        placeholder="Select a time..."
                    />
                </Field>
            );
        }
        else
        {
            return undefined;
        }
    }, [onLifeTimeTimeChange, record.guid, record.lifeTime, styles.grow]);

    const expiryFields = React.useMemo(() =>
    {
        if (record.lifeTime?.kind === "absolute" || record.lifeTime?.kind === "relative")
        {
            return (
                <>
                    <Field
                        className={styles.grow}
                        label={
                            <InfoLabel info="Expiry in UTC. Make sure that this is on the day you want, as it can derivate from the local value due to time zone offsets.">
                                {record.guid ? "Expiry (UTC)" : "Calculated Expiry (UTC)"}
                            </InfoLabel>
                        }
                    >
                        <Input
                            type="text"
                            disabled={true}
                            value={expiryDate?.toISOString() ?? ""}
                            placeholder="Fill out all mandatory expiry information to get a preview expiry"
                        />
                    </Field>

                    <Field
                        className={styles.grow}
                        label={
                            <InfoLabel info="Expiry in local time. Make sure that this is on the day you want, as it can derivate from the UTC value due to time zone offsets.">
                                {record.guid ? "Expiry (Local)" : "Calculated Expiry (Local)"}
                            </InfoLabel>
                        }
                    >
                        <Input
                            type="text"
                            disabled={true}
                            value={expiryDate?.toString() ?? ""}
                            placeholder="Fill out all mandatory expiry information to get a preview expiry"
                        />
                    </Field>
                </>
            );
        }
        else
        {
            return undefined;
        }
    }, [expiryDate, record.guid, record.lifeTime?.kind, styles.grow]);

    const data: ChartProps = React.useMemo(() => {
        const grouped = telemetry.reduce<Record<string, typeof telemetry>>((acc, t) => {
            if (!t.environment?.identifier || !t.environment?.type)
            {
                return acc;
            }

            const key = `${t.environment.type}:${t.environment.identifier}`;
            
            if (!acc[key])
            {
                acc[key] = [];
            }

            acc[key].push(t);

            return acc;
        }, {});

        // assign each group a different color from DataVizPalette
        const palette = [
            DataVizPalette.color1,
            DataVizPalette.color2,
            DataVizPalette.color3,
            DataVizPalette.color4,
            DataVizPalette.color5,
            DataVizPalette.color6,
            DataVizPalette.color7,
            DataVizPalette.color8,
            DataVizPalette.color9,
            DataVizPalette.color10,
            DataVizPalette.color11,
            DataVizPalette.color12,
        ];

        const lineChartData = Object.entries(grouped).map(([key, values], idx) => {
            const first = values[0];
            return {
                legend: first.environment?.name ?? key,
                hideNonActiveDots: true,
                lineOptions: {
                    lineBorderWidth: "4",
                },
                data: values
                    .filter((t) => !!t.date)
                    .map((t) => ({
                        x: new Date(`${t.date}T00:00:00.000Z`),
                        y: t.activeUsers ?? 0,
                    }))
                    .sort((a, b) => a.x.getTime() - b.x.getTime()), // keep lines sorted
                color: palette[idx % palette.length],
            };
        });

        return {
            chartTitle: "Active User Telemetry",
            lineChartData,
        };
    }, [telemetry]);

    const body = React.useMemo(() => (
        <>
            { showPaymentBanner && (
                <MessageBar intent="info">
                    <MessageBarBody>
                        <MessageBarTitle>License key not yet generated</MessageBarTitle>
                        This license was generated using a basic publisher and exceeds the free license lifetime (1 month). If you meanwhile purchased premium or enterprise, just save again to get the license key. Otherwise click the following button to purchase.
                    </MessageBarBody>
                    <MessageBarActions>
                        <Button
                            appearance='primary'
                            onClick={() => navigate(`/checkout?priceId=${priceIdDictionary["single"]}&referenceId=${record.externalId}`)}
                        >
                            Purchase
                        </Button>
                    </MessageBarActions>
                </MessageBar> )
            }
            { !!missingEnvironments.length && (
                <MessageBar intent="info">
                    <MessageBarBody>
                        <MessageBarTitle>Missing environments</MessageBarTitle>
                        The product you selected has not all required environment types available in this license. Missing types: {missingEnvironments.join(", ")}.
                        Add environments of the missing types so that your product will be fully usable in the customer's organization.
                    </MessageBarBody>
                </MessageBar> )
            }

            { showRefreshBanner && (
                <MessageBar intent="success">
                    <MessageBarBody>
                        <MessageBarTitle>License key generation pending</MessageBarTitle>
                            Purchase was successful. License generation is in progress. Refresh to see your key.{" "}
                            <Button
                                appearance='primary'
                                onClick={refresh}
                            >
                                Refresh
                            </Button>
                    </MessageBarBody>
                </MessageBar> )
            }

            <div className={styles.flexColumn}>
                <div className={styles.flexRowWrap}>
                    <div className={styles.growFlexColumn}>
                        <Field
                            label="Product"
                            required
                        >
                            <Dropdown
                                disabled={!!record.guid || !productOptions.length}
                                placeholder="Choose a product to license"
                                value={record?.productId?.name ?? ""}
                                selectedOptions={[record?.productId?.guid ?? ""]}
                                onOptionSelect={onProductChange}
                            >
                                { productOptions.map((p) => <Option key={p.guid} value={p.guid}>{p.name}</Option>) }
                            </Dropdown>
                        </Field>

                        <Field
                            label="Product Version"
                            required
                        >
                            <Dropdown
                                disabled={!!record.guid || !record.productId}
                                placeholder="Choose a version of your product"
                                value={record?.productVersionId?.name ?? ""}
                                selectedOptions={[record?.productVersionId?.guid ?? ""]}
                                onOptionSelect={onProductVersionChange}
                            >
                                { productVersionOptions.map((p) => <Option key={p.guid} value={p.guid}>{p.name}</Option>) }
                            </Dropdown>
                        </Field>

                        <Field
                            label="Customer"
                            required
                        >
                            <Dropdown
                                disabled={!!record.guid || !customerOptions.length}
                                placeholder="Choose a customer to license to"
                                value={record?.customerId?.name ?? ""}
                                selectedOptions={[record?.customerId?.guid ?? ""]}
                                onOptionSelect={onCustomerChange}
                            >
                                { customerOptions.map((p) => <Option key={p.guid} value={p.guid}>{p.name}</Option>) }
                            </Dropdown>
                        </Field>

                        <Field
                            label="Customer Environment"
                            required
                        >
                            <Dropdown
                                placeholder="Choose one or multiple customer environments to license"
                                disabled={!record.customerId || !!record.guid}
                                multiselect={true}
                                value={record?.environments?.map(e => `${e.name} (${e.type} | ${e.identifier})`).join(", ") ?? ""}
                                selectedOptions={record?.environments?.map(e => e.guid) ?? []}
                                onOptionSelect={onCustomerEnvironmentsChange}
                            >
                                { customerEnvironmentOptions.map((e) => <Option key={e.guid} value={e.guid}>{`${e.name} (${e.type} | ${e.identifier})`}</Option>) }
                            </Dropdown>
                        </Field>

                        { !!customProperties.length && 
                            <>
                                { customProperties.map(p => (
                                    <Field
                                        key={p.guid}
                                        id={p.guid}
                                        className={styles.grow}
                                        label={p.displayName}
                                        required={p.requirementLevel === "required"}
                                        hint={p.propertyType === "string" ? `${(record?.customProperties?.find(cp => cp.name === p.name)?.value ?? "").length} / 200` : undefined}
                                    >
                                    {
                                        (["bool"] as Array<PropertyType>).includes(p.propertyType) ? (
                                            <TriStateBoolDropdown
                                                value={record?.customProperties?.find(cp => cp.name === p.name)?.value as boolean | null ?? null}
                                                onChange={(value) => onCustomPropertyChange(p.name, value)}
                                                disabled={!!record.guid}
                                                placeholder={p.requirementLevel === "required" ? "Select true or false" : "Choose a value or leave unset"}
                                            />
                                        )
                                        : (
                                            <Input
                                                type="text"
                                                disabled={!!record.guid}
                                                value={record?.customProperties?.find(cp => cp.name === p.name)?.value?.toString() ?? ""}
                                                onChange={(_, data) => {
                                                    if(p.propertyType === "number") {
                                                        const parsedValue = parseFloat(data.value);
                                                        const numericValue = data.value == null || data.value === "" || isNaN(parsedValue)
                                                            ? null
                                                            : parsedValue;

                                                        onCustomPropertyChange(p.name, numericValue);
                                                    }
                                                    else {
                                                        onCustomPropertyChange(p.name, data.value === "" ? null : data.value);
                                                    }
                                                }}
                                                placeholder="Insert value..."
                                            />
                                        )
                                    }
                                    </Field>
                                )) }
                            </>
                        }

                        <Field
                            label={
                                <InfoLabel
                                    required
                                    info="This defines how the expiry will be calculated. You can either choose a relative expiry (from the current date), an absolute expiry (an explicit date) or unlimited, so no expiry. All information you enter is always UTC, check 'Calculated Expiry (UTC)' and 'Calculated Expiry (Local)' fields for a better understanding."
                                >
                                    License Lifetime Kind
                                </InfoLabel>
                            }
                        >
                            <Dropdown
                                disabled={!!record.guid}
                                placeholder="Choose a lifetime for the license"
                                value={capitalizeFirstChar(record?.lifeTime?.kind) ?? ""}
                                selectedOptions={record?.lifeTime?.kind ? [record.lifeTime.kind] : []}
                                onOptionSelect={onLifeTimeChange}
                            >
                                <Option key="relative" value="relative">Relative</Option>
                                <Option key="absolute" value="absolute">Absolute</Option>
                                <Option key="unlimited" value="unlimited">Unlimited</Option>
                            </Dropdown>
                        </Field>

                        { record?.lifeTime?.kind === "absolute" && (
                            <div className={styles.flexRowWrap}>
                                <Field
                                    className={styles.grow}
                                    label="Expiration Date"
                                >
                                    <Input
                                        type="date"
                                        disabled={!!record.guid}
                                        value={record?.lifeTime?.date ?? ""}
                                        onChange={onLifeTimeDateChange}
                                        placeholder="Select a date..."
                                    />
                                </Field>

                                { modeField }

                                { timeField }

                                { expiryFields }
                            </div>
                        )}

                        { record?.lifeTime?.kind === "relative" && (
                            <div className={styles.flexRowWrap}>
                                <Field
                                    className={styles.grow}
                                    label="Unit"
                                    required
                                >
                                    <Dropdown
                                        disabled={!!record.guid}
                                        placeholder="Choose a unit"
                                        value={capitalizeFirstChar(record?.lifeTime?.unit) ?? ""}
                                        selectedOptions={record?.lifeTime?.unit ? [record.lifeTime.unit] : []}
                                        onOptionSelect={onLifeTimeUnitChange}
                                    >
                                        <Option key="day" value="day">Day</Option>
                                        <Option key="week" value="week">Week</Option>
                                        <Option key="month" value="month">Month</Option>
                                        <Option key="year" value="year">Year</Option>
                                    </Dropdown>
                                </Field>

                                <Field
                                    className={styles.grow}
                                    label="Value"
                                    required
                                >
                                    <Input
                                        type="number"
                                        disabled={!!record.guid}
                                        step={1}
                                        min={1}
                                        value={record?.lifeTime?.value?.toString() ?? ""}
                                        onChange={onLifeTimeValueChange}
                                        placeholder="Select a value for the associated unit..."
                                    />
                                </Field>

                                { modeField }

                                { timeField }

                                { expiryFields }
                            </div>
                        )}

                        <Field
                            label="License Key"
                        >
                            <div className={styles.growFlexRowWrap}>
                                <Textarea
                                    disabled={true}
                                    placeholder={"Generated license key will become visible here"}
                                    value={record.key ?? ""}
                                    style={{ flex: 1 }}
                                />
                                <CopyButton disabled={!record.key} value={record.key} />
                            </div>
                        </Field>
                    </div>

                    { !!id && id !== "new" && !!record.key && 
                        <LicenseExplorer licenseBase64={record.key} />
                    }
                </div>

                { !!telemetry?.length && (
                    <>
                        <h3>Active User Telemetry</h3>
                        <div ref={ref} className={styles.flexRowWrap}>
                            <LineChart
                                data={data}
                                legendsOverflowText={"Overflow Items"}
                                yMinValue={0}
                                yMaxValue={Math.max(...(telemetry.some(t => !!t) ? telemetry.map(t => t.activeUsers ?? 0) : [1000]))}
                                height={(rect?.width ?? 700) / 1.75}
                                width={rect?.width ?? 700}
                                margins={{ left: 35, top: 20, bottom: 35, right: 20 }}
                                getCalloutDescriptionMessage={_calculateCalloutDescription}
                                enablePerfOptimization={true}
                                useUTC={true}
                            />
                        </div>
                    </>
                ) }
            </div>
        </>
    ), [telemetry, data, ref, rect, showRefreshBanner, missingEnvironments, refresh, customerEnvironmentOptions, customerOptions, expiryFields, id, modeField, navigate, customProperties, onCustomPropertyChange, onCustomerChange, onCustomerEnvironmentsChange, onLifeTimeChange, onLifeTimeDateChange, onLifeTimeUnitChange, onLifeTimeValueChange, onProductChange, onProductVersionChange, priceIdDictionary, productOptions, productVersionOptions, record, showPaymentBanner, styles.flexRowWrap, styles.flexColumn, styles.grow, styles.growFlexColumn, styles.growFlexRowWrap, timeField]);

    return (
        <FormBase
            id={id}
            record={record}
            isSaving={isSaving}
            dialogs={dialogs}
            toolbar={toolbar}
            body={body}
            onSave={onSubmit}
        />
    );
};